<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from '@/composable/useI18n'
import type { IMaintenancePhoto } from '@/interfaces/entities/maintenance_photo'
import { formatDateWithTime } from '@/utils/date/date-time'
import { resolveApiAssetUrl } from '@/utils/url/resolve-api-asset-url'

defineProps<{
  photos: IMaintenancePhoto[]
}>()

const { t } = useI18n()
const selectedPhoto = ref<IMaintenancePhoto | null>(null)

function photoSrc(photo: IMaintenancePhoto): string {
  return resolveApiAssetUrl(photo.url)
}

function openPhoto(photo: IMaintenancePhoto) {
  selectedPhoto.value = photo
}

function closePhoto() {
  selectedPhoto.value = null
}
</script>

<template>
  <div class="border border-stroke bg-white shadow-sm">
    <div class="border-b border-stroke bg-gray-2 px-4 py-3">
      <h2 class="text-base font-semibold text-black">{{ t('maintenance_detail.photos.title') }}</h2>
    </div>

    <div v-if="!photos.length" class="p-4 text-sm text-body">
      {{ t('maintenance_detail.photos.empty') }}
    </div>

    <div v-else class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
      <button
        v-for="photo in photos"
        :key="photo.id"
        type="button"
        class="group flex flex-col border border-stroke bg-white text-left transition hover:border-primary"
        @click="openPhoto(photo)">
        <div class="aspect-video w-full overflow-hidden bg-gray-2">
          <img
            :src="photoSrc(photo)"
            :alt="photo.description || t('maintenance_detail.photos.alt', { id: photo.id })"
            class="h-full w-full object-cover transition group-hover:opacity-90"
            loading="lazy" />
        </div>
        <div class="space-y-1 p-3">
          <p class="text-sm font-medium text-black">
            {{ photo.description || t('maintenance_detail.photos.no_description') }}
          </p>
          <p v-if="photo.created_at" class="text-xs text-body">
            {{ formatDateWithTime(photo.created_at) }}
          </p>
        </div>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedPhoto"
        class="fixed inset-0 z-9999 flex items-center justify-center bg-black/70 p-4"
        role="dialog"
        :aria-label="t('maintenance_detail.photos.preview')"
        @click.self="closePhoto">
        <div class="relative max-h-[90vh] max-w-5xl border border-stroke bg-white shadow-lg">
          <button
            type="button"
            class="absolute right-2 top-2 z-10 border border-stroke bg-white px-3 py-1 text-sm font-medium text-black hover:bg-gray-2"
            @click="closePhoto">
            {{ t('maintenance_detail.photos.close') }}
          </button>
          <img
            :src="photoSrc(selectedPhoto)"
            :alt="selectedPhoto.description || t('maintenance_detail.photos.alt', { id: selectedPhoto.id })"
            class="max-h-[80vh] w-full object-contain" />
          <div class="border-t border-stroke p-3">
            <p class="text-sm font-medium text-black">
              {{ selectedPhoto.description || t('maintenance_detail.photos.no_description') }}
            </p>
            <p v-if="selectedPhoto.created_at" class="mt-1 text-xs text-body">
              {{ formatDateWithTime(selectedPhoto.created_at) }}
            </p>
            <a
              :href="photoSrc(selectedPhoto)"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-2 inline-block text-sm font-medium text-primary hover:underline">
              {{ t('maintenance_detail.photos.open_original') }}
            </a>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
